import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  Package2, 
  Users, 
  BookOpen, 
  DollarSign,
  ChevronRight,
  Eye,
  AlertTriangle
} from 'lucide-react';
import { fetchWithAuth } from '../hooks/useAuth';
import { formatPrice } from '../utils/format';

interface DashboardStats {
  totalOrders: number;
  totalProducts: number;
  totalCustomers: number;
  totalRevenue: number;
  businessMetrics: {
    performance: {
      growth: {
        orders: {
          current: number | null;
          previous: number | null;
          growth: number | null;
        };
        revenue: {
          current: number | null;
          previous: number | null;
          growth: number | null;
        };
      };
      orderCompletion: Record<string, number>;
      monthlyRevenue: Record<string, number>;
      customerRetention: {
        repeatCustomers: number | null;
        retentionRate: number | null;
      };
      popularProducts: Array<{
        bookId: number;
        totalQuantity: number;
      }>;
    };
    orders: {
      byStatus: Array<{ status: string; _count: number }>;
      trend: Array<{ createdAt: string; _count: number }>;
      averageValue: number;
    };
    payments: {
      byMethod: Array<{
        method: string;
        _count: number;
        _sum: { amount: number | null };
      }>;
      successRate: Array<any>;
      refunds: {
        count: number;
        amount: number;
      };
    };
    shipping: {
      performance: Array<{
        judet: string | null;
        shippingStatus: string | null;
        _count: number;
      }>;
      regionalAnalytics: {
        successRate: Record<string, { total: number; success: number }>;
        averageDeliveryTime: Record<string, number>;
        failureAnalysis: Record<string, number>;
      };
    };
    products: {
      bestSellers: Array<{
        bookId: number;
        totalQuantity: number;
        totalRevenue: number;
      }>;
      stockTurnover: Array<{
        id: number;
        name: string;
        stock: number;
        monthlySales: number;
        turnoverRate: number;
      }>;
    };
  };
  recentOrders: Array<{
    id: number;
    customerName: string;
    customerEmail: string;
    totalPrice: number;
    status: string;
    createdAt: string;
    items: Array<{
      bookName: string;
      quantity: number;
      price: number;
    }>;
  }>;
  lowStockProducts: Array<{
    id: number;
    name: string;
    stock: number;
    price: number;
    outOfStock: boolean;
  }>;
}

import { samedayStatusConfig } from '../config/sameday.status.config';

const getStatusColor = (status: string): string => {
  // First try to map numeric status IDs
  const numericStatus = parseInt(status);
  if (!isNaN(numericStatus) && samedayStatusConfig.statusMapping[numericStatus]) {
    const mappedStatus = samedayStatusConfig.statusMapping[numericStatus];
    switch (mappedStatus) {
      case 'DELIVERED': return 'bg-green-100 text-green-800';
      case 'IN_TRANSIT': return 'bg-yellow-100 text-yellow-800';
      case 'FAILED': return 'bg-red-100 text-red-800';
      case 'RETURNED': return 'bg-purple-100 text-purple-800';
      case 'CANCELED': return 'bg-red-100 text-red-800';
      case 'REGISTERED': return 'bg-blue-100 text-blue-800';
    }
  }

  // Fallback to string status mapping
  switch (status) {
    case 'PENDING': return 'bg-yellow-100 text-yellow-800';
    case 'CANCELLED': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getStatusLabel = (status: string): string => {
  // First try to map numeric status IDs
  const numericStatus = parseInt(status);
  if (!isNaN(numericStatus) && samedayStatusConfig.statusLabels[numericStatus]) {
    return samedayStatusConfig.statusLabels[numericStatus];
  }

  // Fallback to string status mapping
  switch (status) {
    case 'PENDING': return 'În așteptare';
    case 'CANCELLED': return 'Anulată';
    default: return status;
  }
};

export const DashboardPage: FC = () => {
  const { data: stats } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    refetchInterval: 5000,
    queryFn: async () => {
      const response = await fetchWithAuth('/api/admin/dashboard/stats');
      if (!response.ok) throw new Error('Eroare la încărcarea statisticilor');
      return response.json();
    },
  });

  return (
    <div className="p-6 space-y-8 bg-gray-50 min-h-screen">
      {/* Performance Overview */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {/* Orders stat */}
        <div className="bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 rounded-lg">
          <div className="p-6">
            <div className="flex items-center gap-3">
              <Package2 className="w-8 h-8 text-blue-600" />
              <div>
                <div className="text-sm font-medium text-gray-500">Total Comenzi</div>
                <div className="mt-1 text-3xl font-semibold text-gray-900">
                  {stats?.totalOrders ?? 0} comenzi
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue stat */}
        <div className="bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 rounded-lg">
          <div className="p-6">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-green-600" />
              <div>
                <div className="text-sm font-medium text-gray-500">Total Venituri</div>
                <div className="mt-1 text-3xl font-semibold text-gray-900">
                  {formatPrice(stats?.totalRevenue ?? 0)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customers stat */}
        <div className="bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 rounded-lg">
          <div className="p-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-purple-600" />
              <div>
                <div className="text-sm font-medium text-gray-500">Total Clienți</div>
                <div className="mt-1 text-3xl font-semibold text-gray-900">
                  {stats?.totalCustomers ?? 0} clienți
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Books stat */}
        <div className="bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 rounded-lg">
          <div className="p-6">
            <div className="flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-red-600" />
              <div>
                <div className="text-sm font-medium text-gray-500">Stoc Disponibil</div>
                <div className="mt-1 text-3xl font-semibold text-gray-900">
                  {stats?.totalProducts ?? 0} cărți
                </div>
                <div className="text-sm text-red-600 font-medium mt-1">
                  Stoc redus: {stats?.lowStockProducts?.length ?? 0}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Out of Stock Warning */}
      {stats?.lowStockProducts?.some(product => product.stock === 0) && (
        <div className="mt-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h2 className="text-lg font-semibold text-red-900">Atenție - Stoc Epuizat</h2>
            </div>
            <div className="mt-4 space-y-3">
              {stats.lowStockProducts
                .filter(product => product.stock === 0)
                .map(product => (
                  <div key={product.id} className="flex items-center justify-between bg-white p-3 rounded-md shadow-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{product.name}</span>
                      <span className="text-sm text-red-600 font-medium">Stoc: 0</span>
                    </div>
                    <Link
                      to={`/products/${product.id}`}
                      className="text-red-600 hover:text-red-800 font-medium text-sm flex items-center gap-1"
                    >
                      <Eye className="w-4 h-4" />
                      Verifică Produsul
                    </Link>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Recent orders table */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Package2 className="w-5 h-5 text-gray-600" />
          Ultimele Comenzi
        </h2>
        <div className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200 rounded-lg">
          {!stats?.recentOrders?.length ? (
            <div className="p-5 text-center text-gray-500">
              Nu există comenzi înregistrate
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acțiuni
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stats.recentOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50 transition-colors duration-200">
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="font-medium text-gray-900">#{order.id}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.createdAt).toLocaleDateString('ro-RO')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div>
                          <div className="font-medium text-gray-900">{order.customerName}</div>
                          <div className="text-gray-500 text-xs">{order.customerEmail}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="font-medium text-gray-900">
                        {formatPrice(order.totalPrice)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1.5 inline-flex text-xs leading-5 font-semibold rounded-full shadow-sm ring-1 ring-inset ${
                          getStatusColor(order.status)
                        }`}>
                          {getStatusLabel(order.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <Link
                          to={`/orders/${order.id}/`}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-900 font-medium"
                        >
                          <Eye className="w-4 h-4" />
                          Detalii
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
